import React from 'react';
import ReactDOM from 'react-dom';
import App from './app';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { StyledEngineProvider } from '@mui/material/styles';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import './styles/main.css';
import { analytics } from './utils/analytics';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ROUTE_PATH } from './routes/route-paths';
import { AuthProvider } from './contexts/auth-context';
import { StickyStateKeys } from './hooks/utils/useStickyState';
import { SettingsModalProvider } from './contexts/settings-modal-context';
import { CrisisModalProvider } from './contexts/crisis-modal-context';
import { FiProvider } from './contexts/fi-context';
import { PeersonaProvider } from './contexts/peersona-context';
import { AssistantWidgetProvider } from './contexts/assistant-widget-context';
import ThemeProvider from './providers/theme-injection';
import { resolveTheme, applyThemeToDocument } from './themes';

// Apply theme before first paint to avoid flash
applyThemeToDocument(resolveTheme());

if (process.env.REACT_APP_MWA_GTAG_ID) {
  analytics.init(process.env.REACT_APP_MWA_GTAG_ID);
} else {
  console.error('Could not initialize analytics', process.env.REACT_APP_MWA_GTAG_ID);
}
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      onError: (err: any) => {
        if (err.response?.status === 403) {
          localStorage.removeItem(StickyStateKeys.AuthorizationToken);
          sessionStorage.setItem('loggedIn', 'false');
          window.location.href = ROUTE_PATH.LANDING_PAGE;
        }
      },
    },
  },
});

ReactDOM.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <StyledEngineProvider injectFirst>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <AuthProvider>
            <BrowserRouter>
              <ThemeProvider>
                <SettingsModalProvider>
                  <CrisisModalProvider>
                    <AssistantWidgetProvider>
                      <FiProvider>
                        <PeersonaProvider
                          initialChatbotId={
                            process.env.REACT_APP_PEERSONA_DEMO_CHATBOT_ID
                              ? Number(process.env.REACT_APP_PEERSONA_DEMO_CHATBOT_ID)
                              : null
                          }
                        >
                          <App />
                        </PeersonaProvider>
                      </FiProvider>
                    </AssistantWidgetProvider>
                  </CrisisModalProvider>
                </SettingsModalProvider>
              </ThemeProvider>
            </BrowserRouter>
          </AuthProvider>
        </LocalizationProvider>
      </StyledEngineProvider>
    </QueryClientProvider>
  </React.StrictMode>,
  document.getElementById('root'),
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
